import * as React from "react";
import * as moment from "moment";
import {
    Policy,
    POLICY_STATUS_MTC,
} from "@ferry/journey-services";
import {resourcesHelper} from "@ferry/portal-core";
import {ImgTileLink} from "../../../../components/ImgTileLink";

interface IDriveAwayTilesProps {
    policies?: Policy[];
    policyNumber: string;
    hasMultiplePolicies?: boolean;
}

function prepareFlags(policies: Policy[] | undefined) {
    const hasPolicies = policies && policies.length > 0;
    const policy: Policy | null = policies && policies.length > 0 ? policies[0] : null;
    const hasActivePolicy: boolean = policy != null && moment.utc(policy.endDate, moment.ISO_8601).isSameOrAfter(moment(), 'day');
    const policyCanceled = policy != null && policy.status === POLICY_STATUS_MTC;
    const futureCancelledPolicyBeforeInception: boolean = policy != null && moment.utc(policy.endDate, moment.ISO_8601).isSame(moment.utc(policy.startDate, moment.ISO_8601))
        && moment.utc(policy.startDate, moment.ISO_8601).isAfter(moment(), 'day');
    return {
        hasPolicies,
        policy,
        hasActivePolicy,
        policyCanceled,
        futureCancelledPolicyBeforeInception
    };
}

export const DriveAwayTiles: React.FC<IDriveAwayTilesProps> = (props) => {
    const {
        policies,
        policyNumber,
        hasMultiplePolicies,
    } = props;

    const {
        hasPolicies,
        policy,
        hasActivePolicy,
        policyCanceled,
        futureCancelledPolicyBeforeInception
    } = prepareFlags(policies);

    const cancelPolicyUrl: string = '/journey/init/product/DriveAwayInsurance/process/mtc/presetPage/' + policy?.interaction!._id;

    return (
        <div className="link-grid">
            <div className="d-flex flex-wrap">

                {!futureCancelledPolicyBeforeInception && !(!hasActivePolicy && policyCanceled) && (
                    <div className="flexItem policy-summary">
                        <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/policy-summary.svg`}
                                     alt="Policy summary icon"
                                     to={`/dashboard/driveAway/policySummary/${policyNumber}`}
                                     hasInfoButton={true}
                                     infoModalTitle="Your car policy"
                                     infoModalBulletPoints={["View a short breakdown of your car policy"]}
                                     infoModalButton="Policy summary"
                                     {...props}>
                            Policy summary <span className="arrow-right"/>
                        </ImgTileLink>
                    </div>
                )}

                {hasPolicies && <div className="flexItem documents">
                    <ImgTileLink id="policyDocumentsTile"
                                 src={`/${resourcesHelper.THEME_PATH}/img/documents.svg`}
                                 alt="Documents icon"
                                 to={`/dashboard/driveAwayDocuments/${policyNumber}`}
                                 hasInfoButton={true}
                                 infoModalTitle="Documents"
                                 infoModalBulletPoints={["View your policy documents", "View your old policy documents",
                                     "Request documents to be sent by post"]}
                                 {...props}>
                        Documents <span className="arrow-right"/>
                    </ImgTileLink>
                </div>}

                {hasPolicies && <div className="flexItem make-a-claim">
                    <ImgTileLink id="claimsTile"
                                 src={`/${resourcesHelper.THEME_PATH}/img/make-a-claim.svg`}
                                 alt="Make a claim icon"
                                 to={`/dashboard/pickMotorClaimKind/${policyNumber}`}
                                 hasInfoButton={true}
                                 infoModalTitle="Make a claim"
                                 infoModalBulletPoints={["Make a windscreen or window claim",
                                     "Make other claims (road traffic accident, theft, misfuelling, lost/stolen keys, etc."]}
                                 {...props}>
                        Make a claim <span className="arrow-right"/>
                    </ImgTileLink>
                </div>}

                <div className="flexItem quotes">
                    <ImgTileLink id="getQuoteTile"
                                 src={`/${resourcesHelper.THEME_PATH}/img/my-quote.svg`}
                                 alt="Quotes icon"
                                 to={`/dashboard/getNewQuote`}
                                 routeState={{
                                     policy,
                                 }}
                                 hasInfoButton={true}
                                 infoModalTitle={"My quote(s)"}
                                 infoModalBulletPoints={["View your car insurance quote", "Get a new car insurance quote"]}
                                 infoModalButton="Get a quote"
                                 {...props}>
                        My quote(s) <span className="arrow-right"/>
                    </ImgTileLink>
                </div>

                {hasPolicies && !hasMultiplePolicies && <div className="flexItem marketing-preferences">
                    <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/marketing-preferences.svg`}
                                 alt="Marketing preferences"
                                 to={"/dashboard/account/marketingPreferences"}
                                 hasInfoButton={true}
                                 infoModalTitle="Marketing preferences"
                                 infoModalBulletPoints={["Change your marketing preferences (hear about our latest products, services and discounts"]}
                                 {...props}>
                        Marketing preferences <span className="arrow-right"/>
                    </ImgTileLink>
                </div>}

                {hasPolicies && !policyCanceled && (<div className="flexItem cancel-policy">
                        <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/cancel-policy.svg`}
                                     alt="Cancel policy"
                                     to={cancelPolicyUrl}
                                     routeState={{
                                         policy
                                     }}
                                     hasInfoButton={true}
                                     infoModalTitle="Cancel policy"
                                     infoModalBulletPoints={["Cancel your policy"]}
                                     {...props}>
                            Cancel policy <span className="arrow-right"/>
                        </ImgTileLink>
                    </div>
                )}

                <div className="flexItem help">
                    <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/help.svg`}
                                 alt="Help icon"
                                 to={`/dashboard/help/${policyNumber}`}
                                 openInNewWindow={false}
                                 hasInfoButton={true}
                                 infoModalTitle="Help"
                                 infoModalBulletPoints={["View 'How to' guides",
                                     "Read FAQs about your cover, account, payment, breakdown, renewal, claims, NCD..."]}
                                 {...props}>
                        Help <span className="arrow-right"/>
                    </ImgTileLink>
                </div>

                {!hasMultiplePolicies && <div className="flexItem account-details">
                    <ImgTileLink src={`/${resourcesHelper.THEME_PATH}/img/account-details.svg`} alt="Account details"
                                 to={"/dashboard/multiPolicy/account"}
                                 {...props}>
                        Account details <span className="arrow-right"/>
                    </ImgTileLink>
                </div>}

            </div>
        </div>
    );
}
